/**
 * Subscription Plans Configuration
 *
 * Loads live values from Firestore (adminConfig/subscriptionPlans) at startup
 * and merges them into the default values below. Admin changes apply in real-time
 * on the next page load (or immediately via onSnapshot if enabled).
 *
 * Plan structure (3 user types):
 *   - Lawyers: LAWYER_FREE, LAWYER_AFTER_FREE, LAWYER_BASIC, LAWYER_PREMIUM
 *   - Clients: CLIENT_FREE, CLIENT_AFTER_FREE
 *   - Law Students: STUDENT_FREE, STUDENT_AFTER_FREE, STUDENT_SIX_MONTHS, STUDENT_ONE_YEAR
 *
 * After Free plans: auto-fallback when free trial expires.
 *   durationDays === 0 means infinite (active until another plan is manually assigned).
 */

import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

// ============================================================================
// DEFAULT VALUES (used as fallback if Firestore is unreachable)
// ============================================================================
const DEFAULTS = {
  FREE_PLAN_CASE_LIMIT: 25,
  CLIENT_FREE_PLAN_CASE_LIMIT: 250,
  FREE_TIER_ALWAYS_UNLOCKED: 25,
  PAID_TIER_BASELINE_UNLOCKED: 20,

  TEAM_MEMBERS_FREE_ACTIVE: 10,
  TEAM_MEMBERS_FREE_EXPIRED: 5,
  TEAM_MEMBERS_BASIC: 5,
  TEAM_MEMBERS_PREMIUM: 10,

  // ---- Lawyer Plans ----
  LAWYER_FREE: {
    name: 'Free Plan',
    category: 'free',
    durationDays: 15,
    caseLimit: 250,
    disposedCaseLimit: 50,
    matesSharingLimit: 10,
    teamMembersLimit: 10,
    features: [
      'Import cases from courts up to 250',
      'Hearing reminders',
      'Case sharing up to 10 mates',
      'Bare acts',
      'Live news',
      'Next hearing date auto update',
    ],
  },

  LAWYER_AFTER_FREE: {
    name: 'After Free (Expired)',
    category: 'expired-free',
    durationDays: 0,
    caseLimit: 5,
    disposedCaseLimit: 5,
    matesSharingLimit: 2,
    teamMembersLimit: 5,
    features: [
      'Limited case access',
      'Hearing reminders',
      'Upgrade to unlock full features',
    ],
  },

  LAWYER_BASIC: {
    name: 'BASIC',
    price: 249,
    priceINR: 249,
    priceUSD: 7,
    priceDisplay: '₹249',
    priceDisplayINR: '₹249',
    priceDisplayUSD: '$7',
    duration: '/month',
    durationDays: 30,
    category: 'Lawyer-Basic',
    iosProductId: 'LAWYER_BASIC_MONTHLY',
    caseLimit: 50,
    disposedCaseLimit: 50,
    matesSharingLimit: 5,
    teamMembersLimit: 5,
    features: [
      'All features in free plan',
      'Import cases from courts up to 50',
      'Case sharing with 5 mates',
      'AI chatbot',
      'Client Connect',
    ],
  },

  LAWYER_PREMIUM: {
    name: 'PREMIUM',
    price: 499,
    priceINR: 499,
    priceUSD: 12,
    priceDisplay: '₹499',
    priceDisplayINR: '₹499',
    priceDisplayUSD: '$12',
    duration: '/month',
    durationDays: 30,
    category: 'Lawyer-Premium',
    iosProductId: 'LAWYER_PREMIUM_MONTHLY',
    caseLimit: 250,
    disposedCaseLimit: 100,
    matesSharingLimit: 10,
    teamMembersLimit: 10,
    features: [
      'All features in basic plan',
      'Import cases from courts up to 250',
      'Case sharing with up to 10 mates',
    ],
  },

  LAWYER_ENTERPRISE: {
    name: 'ENTERPRISE',
    priceDisplay: 'Custom Pricing',
    duration: '',
    category: 'Lawyer-Enterprise',
    features: [
      'Court Cases Export (based on the needs)',
      'Unlimited team members',
      'Private server or custom domain',
      'Dedicated account manager',
      'Integration with law firm CRM',
    ],
  },

  // ---- Client Plans ----
  CLIENT_FREE: {
    name: 'Free Plan',
    category: 'client-free',
    durationDays: 15,
    caseLimit: 250,
    disposedCaseLimit: 50,
    matesSharingLimit: 10,
    teamMembersLimit: 10,
    features: [
      'Import cases from courts up to 250',
      'Hearing reminders',
      'Case sharing up to 10 mates',
      'Live news',
      'Next hearing date auto update',
      'Billing system',
      'Lawyer-client case information sync',
    ],
  },

  CLIENT_AFTER_FREE: {
    name: 'After Free (Expired)',
    category: 'client-expired-free',
    durationDays: 0,
    caseLimit: 5,
    disposedCaseLimit: 5,
    matesSharingLimit: 2,
    teamMembersLimit: 5,
    features: [
      'Limited case access',
      'Hearing reminders',
      'Upgrade to unlock full features',
    ],
  },

  CLIENT_ENTERPRISE: {
    name: 'Custom Enquiry',
    priceDisplay: 'Custom Pricing',
    duration: '',
    category: 'Client-Enterprise',
    features: [
      'Court Cases Export (based on the needs)',
      'Track expenses and invoices for the cases',
      'Custom domain and custom development',
      'Dedicated account manager',
      'Real time updates from the lawyers empanelled on the cases',
    ],
  },

  // ---- Law Student Plans ----
  STUDENT_FREE: {
    name: 'Free Plan',
    category: 'student-free',
    durationDays: 15,
    caseLimit: 250,
    disposedCaseLimit: 50,
    matesSharingLimit: 10,
    teamMembersLimit: 10,
    features: [
      'Import cases from courts up to 250',
      'Hearing reminders',
      'Case sharing up to 10 mates',
      'Bare acts',
      'Live news',
    ],
  },

  STUDENT_AFTER_FREE: {
    name: 'After Free (Expired)',
    category: 'student-expired-free',
    durationDays: 0,
    caseLimit: 5,
    disposedCaseLimit: 5,
    matesSharingLimit: 2,
    teamMembersLimit: 5,
    features: [
      'Limited case access',
      'Hearing reminders',
      'Upgrade to unlock full features',
    ],
  },

  STUDENT_SIX_MONTHS: {
    name: '6 Months',
    price: 5000,
    priceDisplay: '₹5000',
    duration: '6 months',
    durationDays: 182,
    category: 'lawStudent',
    iosProductId: 'JURIDENT_LAW_SIX_MONTHS',
    caseLimit: 250,
    disposedCaseLimit: 50,
    matesSharingLimit: 10,
    teamMembersLimit: 10,
    features: [
      'Confirm your internship through our application by applying in the internship section',
      'Access to lawyer sessions',
    ],
  },

  STUDENT_ONE_YEAR: {
    name: 'Yearly',
    price: 7500,
    priceDisplay: '₹7500',
    duration: '1 year',
    durationDays: 365,
    category: 'lawStudent',
    iosProductId: 'JURIDENT_LAW_ONE_YEAR',
    caseLimit: 250,
    disposedCaseLimit: 50,
    matesSharingLimit: 10,
    teamMembersLimit: 10,
    features: [
      'Confirm your internship through our application by applying in the internship section',
      'Access to lawyer sessions',
    ],
  },

  // Backward compat aliases (old keys → new keys)
  FREE_PLAN: null, // filled in init
  CLIENT_FREE_PLAN: null,
  LAW_STUDENT_SIX_MONTHS: null,
  LAW_STUDENT_ONE_YEAR: null,
};

// Set backward-compat aliases
DEFAULTS.FREE_PLAN = DEFAULTS.LAWYER_FREE;
DEFAULTS.CLIENT_FREE_PLAN = DEFAULTS.CLIENT_FREE;
DEFAULTS.LAW_STUDENT_SIX_MONTHS = DEFAULTS.STUDENT_SIX_MONTHS;
DEFAULTS.LAW_STUDENT_ONE_YEAR = DEFAULTS.STUDENT_ONE_YEAR;

// ============================================================================
// LIVE CONFIG (backing object — mutated in place when Firestore loads)
// ============================================================================
const SubscriptionPlansConfig = {
  ...JSON.parse(JSON.stringify(DEFAULTS)),

  // ==========================================
  // HELPER METHODS
  // ==========================================
  getPlanByCategory(category) {
    switch (category) {
      case 'Lawyer-Basic':
        return this.LAWYER_BASIC;
      case 'Lawyer-Premium':
        return this.LAWYER_PREMIUM;
      case 'Lawyer-Enterprise':
        return this.LAWYER_ENTERPRISE;
      case 'lawStudent':
        return this.STUDENT_SIX_MONTHS;
      default:
        return {};
    }
  },

  isPlanPopular(category) {
    return category === 'Lawyer-Premium';
  },

  getLawyerPlanCategories() {
    return ['Lawyer-Basic', 'Lawyer-Premium', 'Lawyer-Enterprise'];
  },

  getLawStudentPlanCategories() {
    return ['lawStudent'];
  },

  formatPrice(price) {
    return `₹${price}`;
  },

  getDurationDays(category, lawStudentSelection = null) {
    if (category === 'lawStudent') {
      if (lawStudentSelection === null) return this.STUDENT_SIX_MONTHS.durationDays;
      return lawStudentSelection === 0
        ? this.STUDENT_SIX_MONTHS.durationDays
        : this.STUDENT_ONE_YEAR.durationDays;
    }

    switch (category) {
      case 'Lawyer-Basic':
        return this.LAWYER_BASIC.durationDays;
      case 'Lawyer-Premium':
        return this.LAWYER_PREMIUM.durationDays;
      default:
        return 30;
    }
  },

  getPrice(category, lawStudentSelection = null) {
    if (category === 'lawStudent') {
      if (lawStudentSelection === null) return this.STUDENT_SIX_MONTHS.price;
      return lawStudentSelection === 0
        ? this.STUDENT_SIX_MONTHS.price
        : this.STUDENT_ONE_YEAR.price;
    }

    switch (category) {
      case 'Lawyer-Basic':
        return this.LAWYER_BASIC.price;
      case 'Lawyer-Premium':
        return this.LAWYER_PREMIUM.price;
      default:
        return 0;
    }
  },

  /**
   * Get the After Free (fallback) plan for a given user type.
   * Returns the plan config with reduced limits that applies
   * when the free trial has expired and no other plan is assigned.
   */
  getAfterFreePlan(userType) {
    switch (userType) {
      case 'client':
        return this.CLIENT_AFTER_FREE;
      case 'lawStudent':
      case 'Law student':
        return this.STUDENT_AFTER_FREE;
      default:
        return this.LAWYER_AFTER_FREE;
    }
  },

  /**
   * Get case limit by subscription tier.
   * If tier is 'expired-free' (or similar after-free categories),
   * returns the After Free limit.
   */
  getCaseLimitByTier(tier, isClient = false) {
    // After Free categories
    if (tier === 'expired-free') return this.LAWYER_AFTER_FREE.caseLimit ?? 5;
    if (tier === 'client-expired-free') return this.CLIENT_AFTER_FREE.caseLimit ?? 5;
    if (tier === 'student-expired-free') return this.STUDENT_AFTER_FREE.caseLimit ?? 5;

    switch (tier) {
      case 'Lawyer-Basic':
        return this.LAWYER_BASIC.caseLimit ?? 50;
      case 'Lawyer-Premium':
        return this.LAWYER_PREMIUM.caseLimit ?? 250;
      case 'Lawyer-Enterprise':
        return Infinity;
      case 'lawStudent':
        return this.STUDENT_SIX_MONTHS.caseLimit ?? 250;
      default:
        return isClient ? this.CLIENT_FREE_PLAN_CASE_LIMIT : this.FREE_PLAN_CASE_LIMIT;
    }
  },

  /**
   * Get case limit for a user who has no active subscription (expired free trial).
   * Falls back to After Free limits based on user type.
   */
  getExpiredCaseLimit(userType) {
    const afterFree = this.getAfterFreePlan(userType);
    return afterFree?.caseLimit ?? 5;
  },

  /**
   * Get disposed case limit for a given tier.
   */
  getDisposedCaseLimitByTier(tier) {
    if (tier === 'expired-free') return this.LAWYER_AFTER_FREE.disposedCaseLimit ?? 5;
    if (tier === 'client-expired-free') return this.CLIENT_AFTER_FREE.disposedCaseLimit ?? 5;
    if (tier === 'student-expired-free') return this.STUDENT_AFTER_FREE.disposedCaseLimit ?? 5;

    switch (tier) {
      case 'Lawyer-Basic':
        return this.LAWYER_BASIC.disposedCaseLimit ?? 50;
      case 'Lawyer-Premium':
        return this.LAWYER_PREMIUM.disposedCaseLimit ?? 100;
      case 'lawStudent':
        return this.STUDENT_SIX_MONTHS.disposedCaseLimit ?? 50;
      default:
        return 50;
    }
  },

  /**
   * Get mates sharing limit for a given tier.
   */
  getMatesSharingLimitByTier(tier) {
    if (tier === 'expired-free' || tier === 'client-expired-free' || tier === 'student-expired-free') {
      return 2;
    }

    switch (tier) {
      case 'Lawyer-Basic':
        return this.LAWYER_BASIC.matesSharingLimit ?? 5;
      case 'Lawyer-Premium':
        return this.LAWYER_PREMIUM.matesSharingLimit ?? 10;
      case 'lawStudent':
        return this.STUDENT_SIX_MONTHS.matesSharingLimit ?? 10;
      default:
        return 10;
    }
  },

  getAlwaysUnlockedCount(tier) {
    switch (tier) {
      case 'Lawyer-Basic':
      case 'Lawyer-Premium':
      case 'Lawyer-Enterprise':
      case 'lawStudent':
        return this.PAID_TIER_BASELINE_UNLOCKED;
      default:
        return this.FREE_TIER_ALWAYS_UNLOCKED;
    }
  },

  getTeamMembersLimitByTier(tier, isActive) {
    // After Free tiers
    if (tier === 'expired-free' || tier === 'client-expired-free' || tier === 'student-expired-free') {
      return this.TEAM_MEMBERS_FREE_EXPIRED;
    }

    if (tier === 'Lawyer-Enterprise') return 999999;
    if (tier === 'Lawyer-Premium') return this.TEAM_MEMBERS_PREMIUM;
    if (tier === 'Lawyer-Basic') return this.TEAM_MEMBERS_BASIC;
    if (isActive) return this.TEAM_MEMBERS_FREE_ACTIVE;
    return this.TEAM_MEMBERS_FREE_EXPIRED;
  },

  /**
   * Get the free plan config for a given user type.
   */
  getFreePlan(userType) {
    switch (userType) {
      case 'client':
        return this.CLIENT_FREE;
      case 'lawStudent':
      case 'Law student':
        return this.STUDENT_FREE;
      default:
        return this.LAWYER_FREE;
    }
  },

  /**
   * Find a plan config by category (wider coverage than getPlanByCategory).
   * Falls back to the appropriate Free plan if category is null/unknown.
   */
  findPlanByCategory(category, userType = null) {
    const planKeys = [
      'LAWYER_FREE', 'LAWYER_AFTER_FREE', 'LAWYER_BASIC', 'LAWYER_PREMIUM', 'LAWYER_ENTERPRISE',
      'CLIENT_FREE', 'CLIENT_AFTER_FREE', 'CLIENT_ENTERPRISE',
      'STUDENT_FREE', 'STUDENT_AFTER_FREE', 'STUDENT_SIX_MONTHS', 'STUDENT_ONE_YEAR',
    ];
    if (category) {
      for (const key of planKeys) {
        if (this[key] && this[key].category === category) return this[key];
      }
    }
    return this.getFreePlan(userType);
  },

  /**
   * Get the featureFlags map for the plan matching category (or free plan fallback).
   */
  getFeatureFlagsByCategory(category, userType = null) {
    const plan = this.findPlanByCategory(category, userType);
    return (plan && plan.featureFlags) || {};
  },
};

// ============================================================================
// FIRESTORE SYNC
// ============================================================================

const FIRESTORE_PATH = ['adminConfig', 'subscriptionPlans'];

/**
 * Merge remote values into the config object, preserving helper methods.
 * Handles both new keys (LAWYER_FREE, STUDENT_SIX_MONTHS) and legacy keys
 * (FREE_PLAN, LAW_STUDENT_SIX_MONTHS).
 */
function applyRemoteConfig(remote) {
  if (!remote) return;

  // Apply all known default keys
  Object.keys(DEFAULTS).forEach((key) => {
    if (remote[key] !== undefined) {
      SubscriptionPlansConfig[key] = remote[key];
    }
  });

  // Backward compat: sync old keys to new keys
  if (remote.LAWYER_FREE && !remote.FREE_PLAN) {
    SubscriptionPlansConfig.FREE_PLAN = remote.LAWYER_FREE;
  }
  if (remote.CLIENT_FREE && !remote.CLIENT_FREE_PLAN) {
    SubscriptionPlansConfig.CLIENT_FREE_PLAN = remote.CLIENT_FREE;
  }
  if (remote.STUDENT_SIX_MONTHS && !remote.LAW_STUDENT_SIX_MONTHS) {
    SubscriptionPlansConfig.LAW_STUDENT_SIX_MONTHS = remote.STUDENT_SIX_MONTHS;
  }
  if (remote.STUDENT_ONE_YEAR && !remote.LAW_STUDENT_ONE_YEAR) {
    SubscriptionPlansConfig.LAW_STUDENT_ONE_YEAR = remote.STUDENT_ONE_YEAR;
  }

  // Forward compat: sync old keys to new keys
  if (remote.FREE_PLAN && !remote.LAWYER_FREE) {
    SubscriptionPlansConfig.LAWYER_FREE = remote.FREE_PLAN;
  }
  if (remote.CLIENT_FREE_PLAN && !remote.CLIENT_FREE) {
    SubscriptionPlansConfig.CLIENT_FREE = remote.CLIENT_FREE_PLAN;
  }
  if (remote.LAW_STUDENT_SIX_MONTHS && !remote.STUDENT_SIX_MONTHS) {
    SubscriptionPlansConfig.STUDENT_SIX_MONTHS = remote.LAW_STUDENT_SIX_MONTHS;
  }
  if (remote.LAW_STUDENT_ONE_YEAR && !remote.STUDENT_ONE_YEAR) {
    SubscriptionPlansConfig.STUDENT_ONE_YEAR = remote.LAW_STUDENT_ONE_YEAR;
  }
}

let _loadPromise = null;
let _loaded = false;
let _unsubscribe = null;

/**
 * Load subscription config from Firestore.
 * Safe to call multiple times — the first call is memoized.
 * Call this at app startup (e.g. in index.js) before rendering gated UI.
 */
export async function loadSubscriptionPlansConfig() {
  if (_loadPromise) return _loadPromise;

  _loadPromise = (async () => {
    try {
      const snap = await getDoc(doc(db, ...FIRESTORE_PATH));
      if (snap.exists()) {
        applyRemoteConfig(snap.data());
        console.log('✅ SubscriptionPlansConfig loaded from Firestore');
      } else {
        console.log('ℹ️ No SubscriptionPlansConfig in Firestore — using defaults');
      }
      _loaded = true;
    } catch (error) {
      console.error('❌ Failed to load SubscriptionPlansConfig from Firestore:', error);
    }
    return SubscriptionPlansConfig;
  })();

  return _loadPromise;
}

/**
 * Subscribe to live changes. Returns an unsubscribe function.
 * Use this if you want admin changes to apply without a page reload.
 */
export function subscribeSubscriptionPlansConfig() {
  if (_unsubscribe) return _unsubscribe;

  _unsubscribe = onSnapshot(
    doc(db, ...FIRESTORE_PATH),
    (snap) => {
      if (snap.exists()) {
        applyRemoteConfig(snap.data());
        _loaded = true;
        console.log('🔄 SubscriptionPlansConfig updated from Firestore');
      }
    },
    (error) => {
      console.error('❌ SubscriptionPlansConfig snapshot error:', error);
    }
  );

  return _unsubscribe;
}

export function isSubscriptionPlansConfigLoaded() {
  return _loaded;
}

export { SubscriptionPlansConfig };
export default SubscriptionPlansConfig;
